/**
 * Turnstile widget lifecycle manager
 * Handles rendering, resetting, and cleanup of Cloudflare Turnstile widgets
 */

import type { TurnstileWidget } from '../types';
import { ConfigStore } from '../state/config-store';

// Extend Window interface for Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export class TurnstileManager {
  private widgets: Map<string, TurnstileWidget> = new Map();
  private currentToken: string | null = null;
  private configStore: ConfigStore;
  private waitingForScript: Array<() => void> = [];
  private scriptLoaded: boolean = false;

  constructor(configStore: ConfigStore) {
    this.configStore = configStore;

    // Wait for Turnstile script
    if (window.turnstile) {
      this.onScriptLoad();
    } else {
      window.onTurnstileLoad = () => this.onScriptLoad();
    }

    // Re-render widgets when site key changes
    configStore.subscribe((config) => {
      if (config.turnstileSiteKey) {
        this.refreshAllWidgets();
      }
    });
  }

  getToken(): string | null {
    return this.currentToken;
  }

  clearToken(): void {
    this.currentToken = null;
  }

  renderWidget(containerId: string): void {
    const render = () => {
      const siteKey = this.configStore.getConfig().turnstileSiteKey;
      if (!siteKey) {
        console.warn('No Turnstile site key configured');
        return;
      }

      const container = document.getElementById(containerId);
      if (!container) {
        console.warn(`Turnstile container not found: ${containerId}`);
        return;
      }

      // Remove existing widget
      this.removeWidget(containerId);

      try {
        const widgetId = window.turnstile!.render(container, {
          sitekey: siteKey,
          callback: (token: string) => {
            this.currentToken = token;
          },
          'expired-callback': () => {
            this.currentToken = null;
          },
        });

        this.widgets.set(containerId, {
          containerId,
          widgetId,
          container,
        });
      } catch (error) {
        console.error(`Failed to render Turnstile widget: ${containerId}`, error);
      }
    };

    if (this.scriptLoaded) {
      render();
    } else {
      this.waitingForScript.push(render);
    }
  }

  resetWidget(containerId: string): void {
    const widget = this.widgets.get(containerId);
    if (widget && widget.widgetId) {
      try {
        window.turnstile?.reset(widget.widgetId);
      } catch (error) {
        // Fallback: re-render
        console.warn('Failed to reset widget, re-rendering:', error);
        this.renderWidget(containerId);
      }
    }
    this.currentToken = null;
  }

  removeWidget(containerId: string): void {
    const widget = this.widgets.get(containerId);
    if (widget && widget.widgetId) {
      try {
        window.turnstile?.remove(widget.widgetId);
      } catch (error) {
        // Widget may already be removed
      }
      widget.container.innerHTML = '';
      this.widgets.delete(containerId);
    }
  }

  removeAllWidgets(): void {
    this.widgets.forEach((_, containerId) => {
      this.removeWidget(containerId);
    });
  }

  refreshAllWidgets(): void {
    const containerIds = Array.from(this.widgets.keys());
    this.removeAllWidgets();
    containerIds.forEach((id) => this.renderWidget(id));
  }

  private onScriptLoad(): void {
    this.scriptLoaded = true;
    this.waitingForScript.forEach((fn) => fn());
    this.waitingForScript = [];
  }
}
